"use client"

import { useState, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/Calendar"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { CalendarIcon, Code2Icon, ServerIcon, LayersIcon, ChevronDownIcon } from "lucide-react"
import { format } from "date-fns"

interface SkillCategory {
    name: string
    icon: JSX.Element
    skills: string[]
  }
  
  interface FilterDropdownProps {
    onFilterChange: (filters: {
      startDate: Date | null
      endDate: Date | null
      skills: string[]
    }) => void
    onClearFilters: () => void
  }
  
  const skillCategories: SkillCategory[] = [
    {
      name: "Frontend",
      icon: <Code2Icon className="w-4 h-4 mr-2" />,
      skills: ["React", "Vue.js", "Angular", "CSS", "JavaScript", "TypeScript", "HTML5"],
    },
    {
      name: "Backend",
      icon: <ServerIcon className="w-4 h-4 mr-2" />,
      skills: ["Node.js", "Python", "Java", "C#", "Ruby", "PHP", "Go"],
    },
    {
      name: "Platform",
      icon: <LayersIcon className="w-4 h-4 mr-2" />,
      skills: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "CI/CD", "DevOps"],
    },
  ]
  
  export default function FilterDropdown({ onFilterChange, onClearFilters }: FilterDropdownProps): JSX.Element {
    // Use undefined instead of null for dates to match Calendar component expectations
    const [startDate, setStartDate] = useState<Date | undefined>(undefined)
    const [endDate, setEndDate] = useState<Date | undefined>(undefined)
    const [selectedSkills, setSelectedSkills] = useState<string[]>([])
    const [isOpen, setIsOpen] = useState<boolean>(false)
  
    const handleSkillChange = useCallback((skill: string): void => {
      setSelectedSkills((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]))
    }, [])
  
    const handleApplyFilters = useCallback((): void => {
      onFilterChange({
        // Convert undefined to null when passing to parent component
        startDate: startDate || null,
        endDate: endDate || null,
        skills: selectedSkills,
      })
      setIsOpen(false)
    }, [startDate, endDate, selectedSkills, onFilterChange])
  
    const handleClearFilters = useCallback((): void => {
      setStartDate(undefined)
      setEndDate(undefined)
      setSelectedSkills([])
      onClearFilters()
      setIsOpen(false)
    }, [onClearFilters])
  
    useEffect(() => {
      if (!isOpen) {
        handleApplyFilters()
      }
    }, [isOpen, handleApplyFilters])
  
    const hasActiveFilters: boolean = !!startDate || !!endDate || selectedSkills.length > 0
  
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between h-12">
            {hasActiveFilters
              ? `Filters (${selectedSkills.length + (startDate ? 1 : 0) + (endDate ? 1 : 0)})`
              : "Filters"}
            <ChevronDownIcon className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 p-4">
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Availability</h3>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <div className="w-full">
                  <Label htmlFor="start-date" className="text-xs">
                    Start Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal h-10 ${!startDate && "text-muted-foreground"}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="w-full">
                  <Label htmlFor="end-date" className="text-xs">
                    End Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal h-10 ${!endDate && "text-muted-foreground"}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-medium mb-2">Skills</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {skillCategories.map((category) => (
                  <div key={category.name}>
                    <h4 className="text-sm font-medium flex items-center mb-1">
                      {category.icon}
                      {category.name}
                    </h4>
                    <div className="ml-6 space-y-2">
                      {category.skills.map((skill) => (
                        <div key={skill} className="flex items-center space-x-2">
                          <Checkbox
                            id={skill}
                            checked={selectedSkills.includes(skill)}
                            onCheckedChange={() => handleSkillChange(skill)}
                            className="h-5 w-5"
                          />
                          <Label htmlFor={skill} className="text-sm font-normal cursor-pointer">
                            {skill}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button onClick={handleClearFilters} variant="outline" className="h-10">
                Clear Filters
              </Button>
              <Button onClick={handleApplyFilters} className="h-10">
                Apply Filters
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  }
  